import React from 'react';
import { Modal, Button, message } from 'antd';
import { CopyOutlined } from '@ant-design/icons';
import { Participant } from '../services/participant';

interface ParticipantDetailsModalProps {
  participant: Participant | null;
  isOpen: boolean;
  onClose: () => void;
}

const ParticipantDetailsModal: React.FC<ParticipantDetailsModalProps> = ({
  participant,
  isOpen,
  onClose
}) => {
  if (!participant) return null;

  const copyToClipboard = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      message.success(`${label} copied to clipboard!`);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        message.success(`${label} copied to clipboard!`);
      } catch (fallbackErr) {
        message.error('Failed to copy to clipboard');
      }
      document.body.removeChild(textArea);
    }
  };

  return (
    <Modal
      open={isOpen}
      onCancel={onClose}
      footer={null}
      width={700}
      centered
      className="participant-details-modal"
      styles={{
        mask: { backgroundColor: 'rgba(0, 0, 0, 0.5)' }
      }}
    >
      <div className="bg-white rounded-lg">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Participant Details</h2>
         
        </div>

        {/* Content */}
        <div className="p-4">
          {/* Profile Section */}
          <div className="text-center mb-6">
            {/* Profile Picture */}
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gray-200 flex items-center justify-center">
              <span className="text-2xl font-semibold text-gray-600">
                {participant.userId?.fullName?.charAt(0).toUpperCase() || 'N/A'}
              </span>
            </div>
            
            {/* Name */}
            <h3 className="text-xl font-bold text-gray-900 mb-1">{participant.userId?.fullName || 'N/A'}</h3>
            
            {/* Role */}
            <p className="text-sm text-gray-600 capitalize">{participant.leader ? 'Leader' : 'Member'}</p>
          </div>

          {/* Details Section */}
          <div className=" grid grid-cols-2 gap-4">
            {/* Email Address */}
            <div className="flex items-center justify-between py-3 border-b border-gray-100">
              <div>
                <p className="text-sm font-medium text-gray-900">Email Address</p>
                <p className="text-sm text-gray-600">{participant.userId?.email || 'N/A'}</p>
              </div>
              <Button
                type="text"
                icon={<CopyOutlined />}
                onClick={() => copyToClipboard(participant.userId?.email || '', 'Email')}
                className="text-gray-500 hover:text-gray-700"
                size="small"
              />
            </div>

            {/* Phone Number */}
            <div className="flex items-center justify-between py-3 border-b border-gray-100">
              <div>
                <p className="text-sm font-medium text-gray-900">Phone number</p>
                <p className="text-sm text-gray-600">{participant.userId?.phoneNumber || 'N/A'}</p>
              </div>
              <Button
                type="text"
                icon={<CopyOutlined />}
                onClick={() => copyToClipboard(participant.userId?.phoneNumber || '', 'Phone number')}
                className="text-gray-500 hover:text-gray-700"
                size="small"
              />
            </div>

            {/* State of Residence */}
            <div className="flex items-center justify-between py-3 border-b border-gray-100">
              <div>
                <p className="text-sm font-medium text-gray-900">State of Residence</p>
                <p className="text-sm text-gray-600">{participant.userId?.state || 'N/A'}</p>
              </div>
            </div>

            {/* Gender */}
            <div className="flex items-center justify-between py-3 border-b border-gray-100">
              <div>
                <p className="text-sm font-medium text-gray-900">Gender</p>
                <p className="text-sm text-gray-600 capitalize">{participant.userId?.gender || 'N/A'}</p>
              </div>
            </div>

            {/* Team */}
            <div className="flex items-center justify-between py-3">
              <div>
                <p className="text-sm font-medium text-gray-900">Team</p>
                <p className="text-sm text-gray-600">
                  {participant.teamId ? participant.teamId.name : 'No Team'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default ParticipantDetailsModal;
